import { inject, Injectable } from '@angular/core';
import { omit } from '@sama/micro-dash';
import { InjectableSuperclass } from '@sama/ng-core';
import {
  BehaviorSubject,
  debounceTime,
  Observable,
  skip,
  switchMap,
  take,
  tap,
} from 'rxjs';
import { environment } from '../../../../environments/environment';
import { TrackingEvent } from '../../../cross-team-shared/event-tracking/event-utils';
import { RollbarService } from '../../../cross-team-shared/messaging/rollbar.service';
import { UuidService } from '../../../cross-team-shared/misc-services/uuid.service';
import releaseSha from '../../../release-sha.json';
import { EventTrackingOptions } from '../../../team-apis/core/event-tracking';
import { User } from '../../../team-apis/otterdam/user';
import {
  sanitizeBody,
  sanitizeHeaders,
  sanitizeUrl,
} from './log-rocket-sanitization';
import { LogRocketService } from './log-rocket.service';

const APP_ID = 'mbdpid/sama';
const LOCAL_STORAGE_KEY = 'debug-logging';
const PERSIST_SESSION_DURATION = 8 * 60 * 60 * 1000; // 8 hours
const SESSION_INACTIVITY_TIMEOUT = 30 * 60 * 1000; // 30 minutes

interface LocalStorageSettings {
  logId: string;
  userId: number;
  expiresAt: number;
}

/**
 * This service manages debug logging using LogRocket. To add temporary logs
 * while troubleshooting an issue, you can simply use `console.debug()` (which
 * will appear in LogRocket).
 */
@Injectable({ providedIn: 'root' })
export class DebugLoggingService extends InjectableSuperclass {
  logId: string;
  debugLoggingEnabled$ = new BehaviorSubject(false);

  private logRocketService = inject(LogRocketService);
  private rollbarService = inject(RollbarService);
  private uuidService = inject(UuidService);
  private currentUser!: User;

  constructor() {
    super();
    this.logId = this.uuidService.getUuidV4();
  }

  /**
   * Start debug logging if necessary. This should be called as early as
   * possible during initialization, but once `currentUser` is available.
   */
  initialize(currentUser: User) {
    this.currentUser = currentUser;
    this.removeExpiredLocalStorage();
    if (this.isLocalStorageValid()) {
      this.logId = this.getLocalStorageSettings().logId;
      this.startRecording();
    }

    this.logRocketService.identify(currentUser.id.toString(), {
      name: currentUser.full_name,
      email: currentUser.email,
    });
  }

  startPersistentRecording(activity$: Observable<unknown>) {
    this.startRecording();
    this.setLocalStorageSettings();
    this.subscribeToInactivity(activity$);
  }

  startRecording() {
    if (this.debugLoggingEnabled$.value) {
      return;
    } else {
      this.debugLoggingEnabled$.next(true);
    }

    this.initLogRocket();
    this.addSessionUrlToRollbar();

    this.trackLogId();

    this.trackReleaseSha();
    this.trackPageLoadType();
    this.trackNetworkDownlink();
  }

  flagIssue(text: string) {
    this.logRocketService.track('Issue flagged', { text });
    this.trackLogId(); // in case the session has changed
    this.trackNetworkDownlink();
  }

  /**
   * Log events to the console during debug logging. Use console.debug instead
   * of LogRocket.track to easily see events alongside network requests.
   */
  trackEvent(event: TrackingEvent, options?: EventTrackingOptions) {
    if (!this.debugLoggingEnabled$.value) {
      return;
    }

    // eslint-disable-next-line no-console -- Intentional debug logging
    console.debug('Event:', event.id, omit(event, 'id'), options);
  }

  trackFeatureFlags(flags: string[]) {
    this.logRocketService.track('Feature flags', { flags });
  }

  private initLogRocket() {
    if (!environment.shouldSendDebugLogs) {
      return;
    }

    this.logRocketService.init(APP_ID, {
      dom: { privateClassNameBlocklist: ['logrocket-disable-recording'] },
      network: {
        requestSanitizer: (request) => {
          request.url = sanitizeUrl(request.url);
          request.headers = sanitizeHeaders(request.headers);
          request.body = sanitizeBody(request.body, request.headers);
          return request;
        },
        responseSanitizer: (response) => {
          response.headers = sanitizeHeaders(response.headers);
          response.body = sanitizeBody(response.body, response.headers);
          return response;
        },
      },
      browser: { urlSanitizer: (url) => sanitizeUrl(url) },
    });
  }

  private addSessionUrlToRollbar() {
    this.logRocketService.getSessionURL((url) => {
      this.rollbarService.configure({
        payload: { logRocketSessionUrl: url },
      });
    });
  }

  private removeExpiredLocalStorage() {
    if (!this.isLocalStorageValid()) {
      localStorage.removeItem(LOCAL_STORAGE_KEY);
    }
  }

  private isLocalStorageValid() {
    const localStorageSettings = this.getLocalStorageSettings();
    const user = this.currentUser;

    return (
      localStorageSettings.expiresAt > Date.now() &&
      localStorageSettings.userId === user.id
    );
  }

  private getLocalStorageSettings(): LocalStorageSettings {
    return JSON.parse(localStorage.getItem(LOCAL_STORAGE_KEY) ?? '{}');
  }

  private setLocalStorageSettings() {
    localStorage.setItem(
      LOCAL_STORAGE_KEY,
      JSON.stringify({
        logId: this.logId,
        userId: this.currentUser.id,
        expiresAt: Date.now() + PERSIST_SESSION_DURATION,
      }),
    );
  }

  private trackLogId() {
    this.logRocketService.track('Log ID', { logId: this.logId });
  }

  private trackReleaseSha() {
    // https://github.com/angular/angular-cli/issues/4318#issuecomment-734402306
    const sha = releaseSha.sha;

    // Allow checking in the console in any environment
    (window as any).releaseSha = sha;

    this.logRocketService.track('Release SHA', { sha });
  }

  private trackPageLoadType() {
    // https://github.com/microsoft/TypeScript/issues/33866
    const navigationEntry = window.performance.getEntriesByType(
      'navigation',
    )[0] as PerformanceNavigationTiming;

    this.logRocketService.track('Page load type', {
      type: navigationEntry.type,
    });
  }

  private trackNetworkDownlink() {
    // Experimental feature to get bandwidth estimate, capped at 10 Mbps.
    // https://stackoverflow.com/a/47511842/318760
    const downlink = (navigator as any).connection?.downlink;
    this.logRocketService.track('Connection downlink', {
      downlink: `${downlink === 10 ? '10+' : (downlink ?? 'unknown')} Mbps`,
    });
  }

  private subscribeToInactivity(activity$: Observable<unknown>) {
    // LogRocket sessions timeout at 30 mins of inactivity, starting a new
    // session once user interacts again. We want to add the logID to each
    // new session after inactivity.

    this.subscribeTo(
      activity$.pipe(
        debounceTime(SESSION_INACTIVITY_TIMEOUT),
        switchMap(() =>
          activity$.pipe(
            // skip first emission so only a new activity triggers the tracking
            skip(1),
            take(1),
            tap(() => {
              this.trackLogId();
            }),
          ),
        ),
      ),
    );
  }
}
